
import { CommonModule, IMAGE_LOADER, ImageLoaderConfig, LocationStrategy, NgOptimizedImage, PathLocationStrategy, registerLocaleData } from '@angular/common';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { DEFAULT_CURRENCY_CODE, ErrorHandler, Injector, LOCALE_ID, NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AppComponent } from './app.component';

import { DemoMaterialModule } from './demo-material-module';
import { FullComponent } from './layouts/full/full.component';
import { AppHeaderComponent } from './layouts/full/header/header.component';
import { AppSidebarComponent } from './layouts/full/sidebar/sidebar.component';

import { DelfiComponentsLibraryModule, WpTokenService } from '@mare-common/ngx-delfi-components';
import { ApiOauthService, ApiWpService, MareAuthModule, TokenServiceAbstract, WpDefaultTokenService } from '@mare-common/ngx-mare-auth';
import { SharedModule } from './shared/shared.module';
import { SpinnerComponent } from './shared/spinner.component';

import localeIt from '@angular/common/locales/it';
import { NgxAssistanceFormModule, NgxSolveupQuestionnaireService } from '@mare-common/ngx-assistance-form';
import { ContractNoticeComponentsModule } from '@mare-common/ngx-contract-notice-components';
import { MatomoRegisterService, NgxMareCommonModule, GlobalErrorHandler, EventInteractionService } from '@mare-common/ngx-mare-common';
import { MareNotificationsModule } from '@mare-common/ngx-mare-notifications';
import { CompanyServiceAbstract, NgxMareUserModule, UserServiceAbstract } from '@mare-common/ngx-mare-user';
import { SelfcaringWebComponentsModule } from '@mare-common/ngx-selfcaring-web-components';
import { WebSolveupLibraryModule, WebSolveupLibraryService, WebSolveupQuestionnaireService, WebSolveupWebsocketService } from '@mare-common/web-solveup-library';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { BandiComponent } from './bandi/bandi.component';
import { CompanyService } from './services/company.service';
import { UserService } from './services/user.service';
import { UserCompanyService } from './services/userCompany.service';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { MultiTranslateHttpLoader } from './shared/multi-translate-http-loader';
import { NgxMareQuestionnaireService } from '@mare-common/ngx-mare-questionnaire';
import { MareLoaderService } from '@mare-common/ngx-mare-loader';
import { PromoService } from './services/promo.service';
import { AppRoutingModule } from './app-routing.module';

import { OAuthModule, OAuthService, OAuthStorage } from 'angular-oauth2-oidc';
import { TokenService } from './services/token.service';
import { PlatformApiOauthService } from './services/platform-api-oauth.service';
import { AppGuard } from './services/guards/app.guard';
import { LoginComponent } from './login/login.component';
import { BrowserModule } from '@angular/platform-browser';
import { PlatformApiWpService } from './services/platform-apiwp.service';
import { ShoppingCartComponent } from './shopping-cart/shopping-cart.component';
import { CompleteProfileComponent } from './complete-profile/complete-profile.component';
import { FooterComponent } from './layouts/full/footer/footer.component';
import { VerifyTokenComponent } from './verify-token/verify-token.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { CommonConfig } from './shared/common-config/common-config';
import { BrowserAnimationsModule, provideAnimations } from '@angular/platform-browser/animations';
import { NavigationService } from './services/navigation.service';
import { Router } from '@angular/router';
import { GoogleAnalyticsService } from './services/google-analytics.service';
import { FullStandaloneComponent } from './layouts/standalone/full-standalone.component';
// import { BollettaComponent } from './bolletta/bolletta.component';
import { DelfiChannelService } from './services/delfi-channel.service';
import { RegistrationRequestComponent } from './registration-request/registration-request.component';
import { PurchaseService } from './services/purchase.service';
// import * as PlotlyJS from 'plotly.js-dist-min';
// import { PlotlyModule } from 'angular-plotly.js';

// PlotlyModule.plotlyjs = PlotlyJS;


registerLocaleData(localeIt);

export function WpTokenServiceFactory(injector: Injector) {
  return new WpTokenService(injector);
}
export function WpDefaultTokenServiceFactory(apiWpService: ApiWpService) {
  return new WpDefaultTokenService(apiWpService);
}


export function UserServiceFactory(userCompanyService: UserCompanyService, tokenService: TokenService) {
  return new UserService(userCompanyService, tokenService);
}

export function CompanyServiceFactory(userCompanyService: UserCompanyService, tokenService: TokenService) {
  return new CompanyService(userCompanyService, tokenService);
}

export function createTranslateLoader(http: HttpClient) {
  const assetsprefixurl = environment.assetsPrefixUrl;
  const prefix = assetsprefixurl ? assetsprefixurl + '/i18n/' : '/account/assets/i18n/';
  return new TranslateHttpLoader(http, prefix, '.json');
}

export function createMultiTranslateLoader(http: HttpBackend) {
  const assetsprefixurl = environment.assetsPrefixUrl;
  const libraries = [
    'ngx-selfcaring-web-components',
    'ngx-contract-notice-components',
  ];
  let conf: any[] = [];

  conf.push({
    prefix: assetsprefixurl + '/i18n/',
    suffix: '.json',
    override: true,
    priority: 1
  });

  libraries.forEach(library => {
    conf.push({ prefix: assetsprefixurl + '/' + library + '/i18n/', suffix: '.json' });
  });


  return new MultiTranslateHttpLoader(http, conf)
}

export function MatomoRegisterServiceFactory() {
  const matomoConfig = {
    trackerUrl: environment.trackUrl,
    siteId: environment.matomoSiteId
  };
  return new MatomoRegisterService(matomoConfig.trackerUrl, matomoConfig.siteId);
}


@NgModule({
  declarations: [
    AppComponent,
    FullComponent,
    FullStandaloneComponent,
    AppHeaderComponent,
    SpinnerComponent,
    BandiComponent,
    LoginComponent,
    CompleteProfileComponent,
    FooterComponent,
    VerifyTokenComponent,
    // BollettaComponent,
    // AssessmentComponent,
    RegistrationRequestComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ShoppingCartComponent,
    NgxMareCommonModule.forRoot(
      {
        isWp: environment.isWp,
        isRouterEnabled: environment.isRouterEnabled,
        isLoggingEnabled: environment.debugEnabled,
      }
    ),
    MareAuthModule.forRoot({
      isWp: environment.isWp,
      // wpConfig: {
      //   serviceEndpoint: 'https://servicecompany.maredigital.it/wp-json',
      //   wpnonce: 'b77e94c602',        
      //   // basicAuth: 'ZnJhbmNlc2NvLnNlc3NhOmR0Q0J2Zzk4MU14REQwVW5saUJ4QkVMNw==' /* ba francesco servicecompany*/
      //   basicAuth: 'Y2xhdWRpb2F1dHVvcmk6UUhvOWl2cjRWU3FUeVI2UlFrbjNIaG1G' /* ba claudio servicecompany*/
      // },
      wpConfig: environment.wpConfig,
      // tokenService: {
      //   provide: TokenServiceAbstract,
      //   useFactory: TokenServiceFactory,
      //   deps: [OAuthService, EventInteractionService]
      // }
      tokenService: {
        provide: TokenServiceAbstract,
        useFactory: TokenServiceFactory,
        deps: [Injector]
      }
    }),
    NgxMareUserModule
      .forRoot({
        userService: {
          provide: UserServiceAbstract,
          useFactory: UserServiceFactory,
          deps: [UserCompanyService, TokenService]
        },
        companyService: {
          provide: CompanyServiceAbstract,
          useFactory: CompanyServiceFactory,
          deps: [UserCompanyService, TokenService]
        }
      })
    ,
    NgOptimizedImage,
    DemoMaterialModule,
    // HttpClientModule,
    SharedModule,
    // RouterModule.forRoot(AppRoutes),
    AppRoutingModule,
    AppSidebarComponent,
    DelfiComponentsLibraryModule.forRoot({
      isWp: environment.isWp,
      isInWp: environment.isInWp,
      wpConfig: environment.wpConfig,
      enableDownloadPreview: true,
      assetsUrl: environment.assetsPrefixUrl,
      // assetsUrl: '/assets',

    }),
    SelfcaringWebComponentsModule,
    ContractNoticeComponentsModule,
    TranslateModule.forRoot({
      defaultLanguage: 'it',
      loader: {
        provide: TranslateLoader,
        useFactory: (createMultiTranslateLoader),
        deps: [HttpBackend]
      }
    }),
    OAuthModule.forRoot(),
    WebSolveupLibraryModule,
    NgxAssistanceFormModule.forRoot({
      enableSolveup: true,
      enableQuestionnaire: true,
      // questionnaireId: 'UUID-QSTN-b58f831a-aba1-4c26-9d3c-dcf3fe46fd99',
      // questionnaireId: 'UUID-QSTN-a0c5e489-bc86-4708-909a-9d7e160d8521',
      // questionnaireId: 'UUID-QSTN-dcd3e460-04f4-4cbb-90b7-1de0a4618a8b',
      questionnaireId: environment.solveupQuestionnaireId || 'UUID-QSTN-dcd3e460-04f4-4cbb-90b7-1de0a4618a8b',
      questionnaireOptions: {
        wrapperClass: 'af-form-assistance-form-questionnaire',
        submitbuttontext: 'Continua',
      },
      questionnaireServiceProvider: {
        provide: NgxMareQuestionnaireService,
        useFactory: (
          // api: WebSolveupApiService,
          // authService: MareAuthService,
          loaderService: MareLoaderService,
          webSolveupLibraryService: WebSolveupLibraryService,
          websocketService: WebSolveupWebsocketService,
          webSolveupQuestionnaireService: WebSolveupQuestionnaireService
        ) => {
          // api.URL = 'http://localhost:8200/ros'
          // return new NgxQuestionnaireService(api, authService, loaderService, webSolveupLibraryService, websocketService);
          return new NgxSolveupQuestionnaireService(loaderService, webSolveupLibraryService, websocketService, webSolveupQuestionnaireService);
        },
        deps: [
          // WebSolveupApiService, 
          // MareAuthService, 
          MareLoaderService,
          WebSolveupLibraryService,
          WebSolveupWebsocketService,
          WebSolveupQuestionnaireService
        ]
      },
      webSolveupConfig: {
        handleOnSuccessMessage:  "Richiesta di assistenza inviata",
        unloadMessage:  "Sei sicuro di voler chiudere la pagina?",
        closedEmergencyMessageByOperator:  "L'operatore ha terminato la sessione",
        defaultWaitMessage:  "Attendere prego...",
        noUserAvailable:  "Non ci sono utenti disponibili",
        noOperatorsAvailable:  "Non ci sono operatori disponibili",
        genericErrorMessage:  "Errore durante la richiesta di assistenza",
        operatorStartsCallMessage:  "Un operatore è entrato in call...",
        closedEmergencyMessageByUser:  "Hai terminato la sessione",
        linkfyEnabled: true,
        enableAudio: true,
        enableVideo: true,
        tryStartChat: false
      },
      textConfig: {
        preSolveupConfig: {
          subtitle: "Scopri il valore di questa informazione",
          title: "Contatta con un consulente specializzato",
          buttonLabel: "Inizia una nuova chat"
        }, 
        formRecapTemplate: {
          title: "Messaggio inviato",
          subtitle: "Sarai ricontattato al più presto",
          thankyou: "Grazie",
          autoclose: "Mi chiudo tra"
        },
        questionnaireTemplate: {
          title: "Hai bisogno di aiuto?",
          subtitle: "Seleziona un'area di interesse"
        },
        solveupTemplate: {
          title: " ",
          subtitle: "Seleziona un'area di interesse"
        },
        staticTemplateChoser: {
          title: "Hai bisogno di aiuto?",
          subtitle: "Usa il form di seguito per inviarci i tuoi feedback, per segnalare eventuali anomalie o anche per richiedere assistenza."
        },
        staticTemplateFromSolveup: {
          title: "Non ci sono operatori disponibili.",
          subtitle: "Usa il form di seguito per segnalare eventuali anomalie o anche per richiedere assistenza."
        }
      }
    }),
    MareNotificationsModule.forRoot({
      assetsPrefixUrl: environment.assetsPrefixUrl
    }),
    // PlotlyModule
  ],
  providers: [
    provideAnimations(),
    {
      provide: IMAGE_LOADER,
      useValue: (config: ImageLoaderConfig) => {
        if (config.src.startsWith('http'))
          return config.src;
        return `${environment.assetsPrefixUrl}${config.src}`;
      }
    },
    {
      provide: ApiWpService,
      useClass: PlatformApiWpService
    },
    {
      provide: ApiOauthService,
      useClass: PlatformApiOauthService
    },
    UserCompanyService,
    AppGuard,
    {
      provide: LocationStrategy,
      useClass: PathLocationStrategy
    },
    { provide: LOCALE_ID, useValue: "it-IT" },
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
    {
      provide: ErrorHandler,
      useFactory: () => {
        // return new GlobalErrorHandler(
        //   {
        //     apiKey:'AIzaSyCxFBoA_w3OYf_mc7eBQD-MIT__uXCW0ZE',
        //     projectId: 'delfi-403115',
        //     scriptVersion: '0.12.0',
        //     sendConfiguration: {
        //       disabled: false,
        //       reportUncaughtExceptions: true,
        //       reportUnhandledPromiseRejections: true,
        //       service: 'mare',
        //       version: '15.0.0'
        //     }
        //   }
        // );
        return new GlobalErrorHandler(
          environment.googleErrorHandlerConfig
        );
      }
    },
    {
      provide: MatomoRegisterService,
      useFactory: MatomoRegisterServiceFactory
    },
    {
      provide: GoogleAnalyticsService,
      useFactory: () => {
        return new GoogleAnalyticsService(environment.googleAnalyticsConfig ?? {isEnabled: false});
      }
    },
    {
      provide: OAuthStorage,
      useFactory: () => {
        return localStorage;
      }
    },
    PromoService,
    DelfiChannelService,
    PurchaseService
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent]
})
export class AppModule { }

// export function TokenServiceFactory(
//   oauthService: OAuthService,
//   eventInteractionService: EventInteractionService) {
//     return new TokenService(oauthService, eventInteractionService);
// }

export function TokenServiceFactory(
  injector: Injector) {
  return injector.get(TokenService);
}
